import type React from 'react';
import { lazy, useMemo } from 'react';
import {
  type RouteObject,
  RouterProvider,
  createBrowserRouter
} from 'react-router';

import AdminRoutes from './AdminPanel/admin-routes';
import Login from './pages/Auth/Login/Login';
import AppProvider from './providers/app.provider';
import Layout from './providers/layout';
import ProtectedRoute from './providers/protected-route';
import { UserPositions } from './shared/constants';
import { ROUTES } from './shared/routes';
import NotFound from './shared/widgets/errors/404';
import { useAppSelector } from './store';

const LeasingScoring = lazy(
  () => import('./pages/LeasingScoring/LeasingScoring')
);
const LeasingScoringCreate = lazy(
  () => import('./pages/LeasingScoringCreate/LeasingScoringCreate')
);
const LeasingScoringRequest = lazy(
  () => import('./pages/LeasingScoringRequest/LeasingScoringRequest')
);
const LeasedProperty = lazy(
  () => import('./pages/LeasedProperty/LeasedProperty')
);
const LeasedPropertyInfo = lazy(
  () => import('./pages/LeasedPropertyInfo/LeasedPropertyInfo')
);
const Contracts = lazy(() => import('./pages/Contracts/Contracts'));
const ContractInfo = lazy(() => import('./pages/ContractInfo/ContractInfo'));
const Clients = lazy(() => import('./pages/Clients/Clients'));
const ClientInfo = lazy(() => import('./pages/ClientInfo/ClientInfo'));
const ClientContractAppeals = lazy(
  () => import('./pages/ClientContractAppeals/ClientContractAppeals')
);
const ClientContractAppeal = lazy(
  () => import('./pages/ClientContractAppeal/ClientContractAppeal')
);
const VehicleAssessment = lazy(
  () => import('./pages/VehicleAssessment/VehicleAssessment')
);
const Assessments = lazy(() => import('./pages/Assessments/Assessments'));
const Reports = lazy(() => import('./pages/Reports/Reports'));
const Statistics = lazy(() => import('./pages/Statistics/Statistics'));

const availableRoutesByModules: Record<
  string,
  (RouteObject & { accesses?: string[] })[]
> = {
  clients: [
    {
      path: ROUTES.clients,
      element: <Clients />
    },
    {
      path: ROUTES.client(),
      element: <ClientInfo />
    }
  ],
  'leasing-scoring': [
    {
      path: ROUTES.leasingScoring,
      element: <LeasingScoring />
    },
    {
      path: ROUTES.leasingScoringCreate,
      element: <LeasingScoringCreate />,
      accesses: [UserPositions.LeasingManager]
    },
    {
      path: ROUTES.leasingScoringRequest(),
      element: <LeasingScoringRequest />
    }
  ],
  'leased-property': [
    {
      path: ROUTES.leasedProperty,
      element: <LeasedProperty />
    },
    {
      path: ROUTES.leasedPropertyCreate,
      element: <LeasedPropertyInfo />
    },
    {
      path: ROUTES.leasedPropertyInfo(),
      element: <LeasedPropertyInfo />
    }
  ],
  contracts: [
    {
      path: ROUTES.contracts,
      element: <Contracts />
    },
    {
      path: ROUTES.contractsCreate,
      element: <ContractInfo />
    },
    {
      path: ROUTES.contract(),
      element: <ContractInfo />
    }
  ],
  'client-contract-appeals': [
    {
      path: ROUTES.clientAppeals,
      element: <ClientContractAppeals />
    },
    {
      path: ROUTES.clientAppeal(),
      element: <ClientContractAppeal />
    }
  ],
  assessments: [
    {
      path: ROUTES.assessments,
      element: <Assessments />
    },
    {
      path: ROUTES.vehicleAssessment(),
      element: <VehicleAssessment />
    }
  ],
  reports: [
    {
      path: ROUTES.reports,
      element: <Reports />
    }
  ],
  statistics: [
    {
      path: ROUTES.statistics,
      element: <Statistics />
    }
  ]
};

const App: React.FC = () => {
  const { userAccesses, userInfo } = useAppSelector((state) => state.userInfo);
  const { menu } = useAppSelector((state) => state.userInfo);

  const router = useMemo(() => {
    const protectedRoutes: RouteObject[] = [];

    if (userAccesses.global.isAdmin) {
      protectedRoutes.push(...AdminRoutes);
    }

    menu.forEach((item) => {
      if (availableRoutesByModules[item.code]) {
        const filteredRoutes = availableRoutesByModules[item.code].filter(
          (route) => {
            if (route.accesses) {
              return route.accesses.includes(userInfo?.position.code ?? '');
            }
            return true;
          }
        );
        protectedRoutes.push(...filteredRoutes);
      }
    });

    return createBrowserRouter([
      {
        path: '/',
        element: <AppProvider />,
        children: [
          {
            path: '/:lang',
            children: [
              {
                path: ROUTES.login,
                element: <Login />
              },
              {
                element: <ProtectedRoute />,
                children: [
                  {
                    element: <Layout />,
                    children: [
                      ...protectedRoutes,
                      {
                        path: '*',
                        element: (
                          <div className="app-router-not-found">
                            <NotFound />
                          </div>
                        )
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]);
  }, [menu, userAccesses.global.isAdmin, userInfo?.position]);

  const routerKey = `${userAccesses.global.isAdmin}:${userInfo?.position?.code ?? ''}:${menu.map((item) => item.code).join(',')}`;

  return <RouterProvider key={routerKey} router={router} />;
};

export default App;
